import { BarsOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, DatePicker, Drawer, Space, Table, Tag, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Allocation, Project } from '../../DTOS/ResourcePlanning';
import api from '../../services/resourcePlanning/ResourcePlanningService';
import AllocationModal, { AllocationDefaults } from './AllocationModal';
import { CellTarget } from './PlanningTable';
import TechnicianDayDrawer from './TechnicianDayDrawer';
import { DATE, DISPLAY_DATE, homeTeam, hours, projectLabel, slotText, useTeams } from './shared';

/** Project staffing separates operational allocation from permanent team membership. */
const ProjectResourcesDrawer: React.FC<{ project: Project; onClose: () => void }> = ({ project, onClose }) => {
  const [, setParams] = useSearchParams();
  const { data: teams = [] } = useTeams();
  const [period, setPeriod] = useState<[Dayjs, Dayjs]>(() => {
    const anchor = dayjs(
      project.startDate > dayjs().format(DATE) || project.endDate < dayjs().format(DATE)
        ? project.startDate
        : undefined,
    );
    const monday = anchor.subtract((anchor.day() + 6) % 7, 'day');
    return [monday, monday.add(27, 'day')];
  });
  const [adding, setAdding] = useState<AllocationDefaults | null>(null);
  const [editing, setEditing] = useState<Allocation | null>(null);
  const [day, setDay] = useState<CellTarget | null>(null);
  const from = period[0].format(DATE),
    to = period[1].format(DATE);
  const {
    data: grid,
    isLoading,
    error,
  } = useQuery({ queryKey: ['resourcePlanning', 'grid', from, to], queryFn: () => api.grid(from, to) });
  const rows = (grid?.allocations || [])
    .filter((row) => row.projectId === project.id)
    .sort((a, b) => a.date.localeCompare(b.date) || a.userName.localeCompare(b.userName));
  const total = rows.reduce((sum, row) => sum + row.minutes, 0),
    people = new Set(rows.map((row) => row.userId));
  const add = () =>
    setAdding({
      projectId: project.id,
      from: project.startDate > from ? project.startDate : from,
      to: project.endDate < to ? project.endDate : to,
    });
  const inPeriod = project.startDate <= to && project.endDate >= from;
  return (
    <>
      <Drawer
        open
        width={1050}
        title={projectLabel(project)}
        aria-label={`Resurse proiect ${project.number}`}
        onClose={onClose}
        destroyOnHidden
      >
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          <Space wrap>
            <Tag color={project.responsibleName ? 'blue' : 'orange'}>
              Responsabil: {project.responsibleName || 'Nedesemnat'}
            </Tag>
            <Tag>{people.size} tehnicieni</Tag>
            <Tag>{hours(total)} planificate</Tag>
          </Space>
          <Space wrap>
            <DatePicker.RangePicker
              value={period}
              allowClear={false}
              format={DISPLAY_DATE}
              onChange={(value) => value?.[0] && value[1] && setPeriod([value[0], value[1]])}
            />
            <Button type='primary' icon={<PlusOutlined />} disabled={!project.isActive || !inPeriod} onClick={add}>
              Aloca tehnicieni
            </Button>
            <Button
              icon={<BarsOutlined />}
              onClick={() => {
                onClose();
                setParams({
                  tab: 'planning',
                  projects: project.id,
                  date: from,
                  range: 'fourWeeks',
                  view: 'gantt',
                  group: 'project',
                });
              }}
            >
              Vezi Gantt
            </Button>
          </Space>
          <Typography.Text type='secondary'>
            Selectati tehnicieni din orice echipa sau fara echipa. Echipa de baza si coordonatorul lor raman
            neschimbati.
          </Typography.Text>
          {error && <Alert type='error' message={(error as Error).message} />}
          <Table<Allocation>
            rowKey='id'
            size='small'
            loading={isLoading}
            dataSource={rows}
            pagination={{ pageSize: 12 }}
            scroll={{ x: 900 }}
            columns={[
              { title: 'Data', dataIndex: 'date', render: (value) => dayjs(value).format(DISPLAY_DATE) },
              { title: 'Tehnician', dataIndex: 'userName' },
              {
                title: 'Echipa / coordonator de baza',
                render: (_, row) => {
                  const home = homeTeam(teams, row.userId, row.date);
                  return (
                    <span>
                      {home?.name || 'Fara echipa'}
                      {home?.leaderName && <Typography.Text type='secondary'> · {home.leaderName}</Typography.Text>}
                    </span>
                  );
                },
              },
              { title: 'Echipa operationala', render: (_, row) => row.teamName || 'Alocare directa' },
              { title: 'Interval', render: (_, row) => slotText(row) },
              { title: 'Ore', render: (_, row) => hours(row.minutes) },
              {
                title: 'Total zi / capacitate',
                render: (_, row) => {
                  const load = grid?.dailyLoads.find((item) => item.userId === row.userId && item.date === row.date);
                  const conflict = grid?.conflicts?.some(
                    (item) => item.userId === row.userId && item.date === row.date,
                  );
                  return (
                    <Space wrap size={4}>
                      <Tag color={load?.overloaded ? 'red' : 'green'}>
                        {hours(load?.minutes || 0)} / {hours(load?.capacity || 480)}
                      </Tag>
                      {conflict && <Tag color='orange'>Suprapunere</Tag>}
                    </Space>
                  );
                },
              },
              {
                title: '',
                render: (_, row) => (
                  <Button
                    size='small'
                    onClick={() => setDay({ teamId: row.teamId, userId: row.userId, date: row.date })}
                  >
                    Gestioneaza ziua
                  </Button>
                ),
              },
            ]}
            locale={{ emptyText: 'Niciun tehnician alocat proiectului in perioada selectata.' }}
          />
        </Space>
      </Drawer>
      {day && grid && (
        <TechnicianDayDrawer
          target={day}
          grid={grid}
          onClose={() => setDay(null)}
          onCreate={(target) =>
            setAdding({
              projectId: project.id,
              userId: target.userId,
              teamId: target.teamId,
              from: target.date,
              to: target.date,
              minutes: target.minutes,
            })
          }
          onEdit={setEditing}
        />
      )}
      {(adding || editing) && (
        <AllocationModal
          key={editing?.id || JSON.stringify(adding)}
          allocation={editing}
          defaults={adding || undefined}
          from={from}
          to={to}
          onClose={() => {
            setAdding(null);
            setEditing(null);
          }}
        />
      )}
    </>
  );
};
export default ProjectResourcesDrawer;
